import { useEffect, useState } from "react";

export type MicOption = { deviceId: string; label: string };

/**
 * Lists the available audio-input devices for the Audio Setup step's mic
 * picker (MDRS-268). Filters out entries Radix's Select can't render: an
 * empty deviceId (some platforms report one before mic permission has ever
 * been granted) throws at runtime for a SelectItem with value="", and a
 * device literally named deviceId "default" collides with this flow's own
 * hardcoded "Default microphone" sentinel option (Radix concatenates both
 * items' text into the trigger when two SelectItems share a value) — the
 * sentinel already covers that meaning, so the browser's duplicate is
 * redundant, not lost.
 */
export const useMicDeviceOptions = () => {
  const [micOptions, setMicOptions] = useState<MicOption[]>([]);

  useEffect(() => {
    let cancelled = false;
    navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        if (cancelled) return;
        setMicOptions(
          devices
            .filter(
              (d) =>
                d.kind === "audioinput" &&
                d.deviceId !== "" &&
                d.deviceId !== "default"
            )
            .map((d, i) => ({
              deviceId: d.deviceId,
              label: d.label || `Microphone ${i + 1}`,
            }))
        );
      })
      .catch((err) => {
        console.warn("[useMicDeviceOptions] enumerateDevices failed:", err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return micOptions;
};
